// src/services/companyService.js
import apiClient from './api';
import axios from 'axios';
import logger from '../utils/logger';

class CompanyService {
    constructor() {
        this.cachedSettings = null;
        this.cacheTimestamp = null;
        this.cacheTimeout = 5 * 60 * 1000; // 5 minutes cache
        this.publicInfoCache = null;
        this.publicInfoCacheTimestamp = null;
    }

    /**
     * Get company settings with caching
     */
    async getCompanySettings(forceRefresh = false) {
        const now = Date.now();
        
        // Return cached data if it exists and is still valid
        if (!forceRefresh && this.cachedSettings && this.cacheTimestamp && (now - this.cacheTimestamp) < this.cacheTimeout) {
            return this.cachedSettings;
        }

        try {
            const response = await apiClient.get('/core/company-settings/');
            logger.log('CompanyService - Raw API response:', response.data);
            logger.log('CompanyService - Logo field:', response.data.logo);
            logger.log('CompanyService - Icon field:', response.data.icon);
            this.cachedSettings = response.data;
            this.cacheTimestamp = now;
            return this.cachedSettings;
        } catch (error) {
            console.error('Error fetching company settings:', error);
            
            // If authentication failed, try to get public info which includes product view settings
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                try {
                    const publicInfo = await this.getPublicCompanyInfo(true);
                    logger.log('CompanyService - Using public info for unauthenticated user:', publicInfo);
                    
                    // Build a partial settings object from public info
                    const partialSettings = {
                        name: publicInfo.name || 'Loading...',
                        logo: publicInfo.logo,
                        icon: publicInfo.icon,
                        address: {},
                        contact: {},
                        financial: {},
                        system_settings: publicInfo.system_settings || {
                            product_views: {
                                gridViewEnabled: true,
                                listViewEnabled: true,
                                wsListViewEnabled: false, // Default to false for security
                                defaultView: 'grid',
                                hideOutOfStock: false
                            }
                        }
                    };
                    
                    // Cache the partial settings
                    this.cachedSettings = partialSettings;
                    this.cacheTimestamp = now;
                    return partialSettings;
                } catch (publicError) {
                    console.error('Error fetching public company info:', publicError);
                }
            }
            
            // Return cached data if available, even if expired
            if (this.cachedSettings) {
                return this.cachedSettings;
            }
            // Return default values if no cache and request fails
            return {
                name: 'Loading...',
                logo: null,
                icon: null,
                address: {},
                contact: {},
                financial: {},
                system_settings: {
                    product_views: {
                        gridViewEnabled: true,
                        listViewEnabled: true,
                        wsListViewEnabled: false, // Changed default to false
                        defaultView: 'grid',
                        hideOutOfStock: false
                    },
                    order_management: {
                        bulkPrintOrdersEnabled: true
                    }
                }
            };
        }
    }

    /**
     * Get company logo URL
     */
    async getCompanyLogo() {
        const settings = await this.getCompanySettings();
        return settings.logo;
    }

    /**
     * Get company icon URL
     */
    async getCompanyIcon() {
        const settings = await this.getCompanySettings();
        return settings.icon;
    }

    /**
     * Get company name
     */
    async getCompanyName() {
        const settings = await this.getCompanySettings();
        return settings.name || 'Company Name';
    }

    /**
     * Get public company info (no authentication required)
     */
    async getPublicCompanyInfo(forceRefresh = false) {
        const now = Date.now();
        
        // Return cached data if it exists and is still valid
        if (!forceRefresh && this.publicInfoCache && this.publicInfoCacheTimestamp && 
            (now - this.publicInfoCacheTimestamp) < this.cacheTimeout) {
            return this.publicInfoCache;
        }

        try {
            // Use axios directly to bypass authentication interceptor
            const API_URL = `${process.env.REACT_APP_API_URL || ''}/api`;
            const response = await axios.get(`${API_URL}/core/company-info/`);
            logger.log('CompanyService - Public info response:', response.data);
            this.publicInfoCache = response.data;
            this.publicInfoCacheTimestamp = now;
            return this.publicInfoCache;
        } catch (error) {
            console.error('Error fetching public company info:', error);
            // Return cached data if available
            if (this.publicInfoCache) {
                return this.publicInfoCache;
            }
            // Return default values
            return {
                name: 'Company',
                logo: null,
                icon: null,
                system_settings: {
                    product_views: {
                        gridViewEnabled: true,
                        listViewEnabled: true,
                        wsListViewEnabled: false,
                        hideOutOfStock: false,
                        defaultView: 'grid'
                    }
                }
            };
        }
    }

    /**
     * Clear cache - useful when settings are updated
     */
    clearCache() {
        this.cachedSettings = null;
        this.cacheTimestamp = null;
        this.publicInfoCache = null;
        this.publicInfoCacheTimestamp = null;
    }

    /**
     * Update settings and refresh cache
     */
    async updateSettings(settingsData) {
        try {
            const response = await apiClient.post('/core/company-settings/', settingsData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });
            
            // Update cache with new data
            this.cachedSettings = response.data;
            this.cacheTimestamp = Date.now();
            
            // Also clear public info cache to ensure consistency
            this.publicInfoCache = null;
            this.publicInfoCacheTimestamp = null;
            
            return response.data;
        } catch (error) {
            console.error('Error updating company settings:', error);
            throw error;
        }
    }
    async getCountries() {
        const response = await apiClient.get('/core/countries/');
        return response.data;
    }

    async createCountry(data) {
        const response = await apiClient.post('/core/countries/create/', data);
        return response.data;
    }

    async updateCountry(countryId, data) {
        const response = await apiClient.patch(`/core/countries/${countryId}/`, data);
        return response.data;
    }

    async deleteCountry(countryId) {
        await apiClient.delete(`/core/countries/${countryId}/delete/`);
    }

    async testCompaniesHouseKey(apiKey) {
        const response = await apiClient.post('/core/integrations/companies-house/test/', { api_key: apiKey || '' });
        return response.data;
    }

    async testVerizon(creds = {}) {
        const response = await apiClient.post('/core/integrations/verizon/test/', {
            verizonBaseUrl: creds.verizonBaseUrl || '',
            verizonAppId: creds.verizonAppId || '',
            verizonUsername: creds.verizonUsername || '',
            verizonPassword: creds.verizonPassword || '',
        });
        return response.data;
    }

    async testZadarma(creds = {}) {
        const response = await apiClient.post('/core/integrations/zadarma/test/', {
            zadarmaApiKey: creds.zadarmaApiKey || '',
            zadarmaApiSecret: creds.zadarmaApiSecret || '',
        });
        return response.data;
    }

    async testMailcow(creds = {}) {
        const response = await apiClient.post('/core/integrations/mailcow/test/', {
            mailcowBaseUrl: creds.mailcowBaseUrl || '',
            mailcowApiKey: creds.mailcowApiKey || '',
            mailcowDomainAdmin: creds.mailcowDomainAdmin || '',
        });
        return response.data;
    }
}

// Export singleton instance
const companyService = new CompanyService();
export default companyService;